import { getAuthBaseUrl } from '../utils/authBaseUrl';

type TCognitoAuthSignupRequest = {
  email: string;
};

type TAuthMeResponse = {
  sub: string;
  email: string;
  [key: string]: unknown;
};

export class AuthApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'AuthApiError';
    this.status = status;
  }
}

// Mints a Cognito user and sets the iwoca_session cookie via auth-service.
// The frontend does not consume the response body — the cookie side-effect
// is what subsequent same-origin requests (like POST /api/lending/edge/signup/)
// rely on for envoy to inject the iwoca-user-id header. 409 means the email
// is already registered somewhere in Cognito; surface via the duplicate-email
// UX rather than a generic error.
export const postAuthSignup = async (
  data: TCognitoAuthSignupRequest,
): Promise<void> => {
  const response = await fetch(`${getAuthBaseUrl()}/signup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new AuthApiError(
      `auth /signup ${response.status} ${response.statusText}`,
      response.status,
    );
  }
};

// Fetches the current Cognito session's identity, or returns null if
// there is no valid session (401 / network error). Used by the signup
// orchestration to distinguish a genuine duplicate email from a retry
// after a mid-flow failure where auth-service already minted the user.
// useAuth.ts has its own fetchCognitoMe wired into React Query; this
// helper is for one-shot imperative checks outside the hook lifecycle.
export const getAuthMe = async (): Promise<TAuthMeResponse | null> => {
  try {
    const response = await fetch(`${getAuthBaseUrl()}/me`, {
      method: 'GET',
      credentials: 'include',
    });
    if (!response.ok) return null;
    return response.json();
  } catch {
    return null;
  }
};
